import React, { useState, useRef } from 'react';
import MenuIcon from '@material-ui/icons/Menu';

import {
  makeStyles,
  alpha,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  Divider
} from '@material-ui/core';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from 'src/redux/slices/authJwt';
import {
  UserCheck,
  Users,
  LogOut,
  ChevronDown,
  Briefcase,
  ClipboardList
} from 'lucide-react';
import qrata from '../../src/qrataLogo.svg';
import {
  NavLink as RouterLink,
  useLocation,
  useHistory
} from 'react-router-dom';
import { PATH_APP } from 'src/routes/paths';

const MOBILE_DRAWER_ITEMS = [
  { label: 'Jobs', icon: Briefcase, tab: 0 },
  { label: 'My Applications', icon: ClipboardList, tab: 1 }
];

const drawerWidth = 256;

const useStyles = makeStyles((theme) => ({
  root: {},
  desktopBar: {
    '@media (max-width: 959.95px)': {
      display: 'none'
    }
  },
  mobileBar: {
    '@media (min-width: 960px)': {
      display: 'none'
    }
  },
  AppBar: {
    boxShadow: 'none',
    zIndex: theme.zIndex.drawer + 1
  },
  mobileHeader: {
    backgroundColor: '#003458',
    top: 0,
    bottom: 'auto'
  },
  mobileToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  toolbarContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    width: '100%'
  },
  drawerPaper: {
    width: drawerWidth,
    background: '#F8FAFC',
    borderRight: '1px solid #E2E8F0'
  },
  drawerNav: {
    padding: '16px 12px'
  },
  drawerItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 12,
    padding: '10px 14px',
    marginBottom: 6,
    borderRadius: 12,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: 600,
    color: '#504949',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    transition: 'background-color 150ms ease, color 150ms ease',
    '&:hover': {
      backgroundColor: 'rgba(226, 232, 240, 0.7)',
      color: '#0F172A'
    }
  },
  drawerItemActive: {
    backgroundColor: '#003853',
    color: '#FFFFFF',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    '&:hover': {
      backgroundColor: '#003853',
      color: '#FFFFFF'
    }
  },

  listItemText: {
    color: '#003458',
    '&:hover': {
      color: 'rgb(54, 176, 154)'
    }
  },
  listItemIcon: {
    color: '#003458',
    '&:hover': {
      color: 'rgb(54, 176, 154)'
    }
  }
}));

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    minWidth: 180,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0'
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5)
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        )
      }
    }
  }
}));

function TopBar({ logoOnly = false }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const anchorRef = useRef(null);
  const { pathname, search } = useLocation();
  const currentTab = Number(new URLSearchParams(search).get('tab')) || 0;
  const [openMenu, setOpenMenu] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isHome = pathname === '/';

  const user = useSelector((state) => state.authJwt?.user);
  const userPhoto = useSelector(
    (state) => state.user?.talentCard?.profilePictureURL
  );
  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    'User';
  const initials = fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('modalClosed');
    dispatch(logout()).then(() => {
      handleClose();
      history.push('/auth/login');
    });
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const renderProfile = (compact = false) => (
    <>
      <Box
        id="accountProfile"
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '6px 12px',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'background-color 150ms ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
            color: '#003458',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 14,
            fontFamily: 'Inter',
            boxShadow: '0 0 0 2px rgba(20, 184, 166, 0.3)',
            overflow: 'hidden'
          }}
        >
          {userPhoto ? (
            <img
              src={userPhoto}
              alt={fullName}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            initials
          )}
        </Box>
        {!compact && (
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'Inter',
              whiteSpace: 'nowrap',
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {fullName}
          </Typography>
        )}
        <ChevronDown size={16} color="rgba(255,255,255,0.7)" />
      </Box>
      <StyledMenu
        id="demo-customized-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            borderRadius: 12,
            minWidth: 224,
            padding: '6px 0',
            border: '1px solid #E2E8F0',
            boxShadow:
              '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            marginTop: 8
          }
        }}
      >
        <Box
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid #F1F5F9',
            marginBottom: 4
          }}
        >
          <Typography
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#1E293B',
              fontFamily: 'Inter'
            }}
          >
            {fullName}
          </Typography>
          <Typography
            style={{
              fontSize: 11,
              color: '#64748B',
              fontFamily: 'Inter'
            }}
          >
            Candidate / Talent
          </Typography>
        </Box>
        <MenuItem
          component={RouterLink}
          to={PATH_APP.general.communities}
          onClick={handleClose}
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: '#334155',
            fontFamily: 'Inter',
            padding: '8px 14px',
            gap: 10
          }}
        >
          <Users size={16} color="#64748B" />
          <span>Communities</span>
        </MenuItem>
        <MenuItem
          component={RouterLink}
          to={PATH_APP.general.talentCard}
          onClick={handleClose}
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: '#334155',
            fontFamily: 'Inter',
            padding: '8px 14px',
            gap: 10
          }}
        >
          <UserCheck size={16} color="#0D9488" />
          <span>Talent Card</span>
        </MenuItem>
        <Divider style={{ margin: '4px 0', background: '#F1F5F9' }} />
        <MenuItem
          onClick={handleLogout}
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: '#334155',
            fontFamily: 'Inter',
            padding: '8px 14px',
            gap: 10
          }}
        >
          <LogOut size={16} color="#64748B" />
          <span>Log out</span>
        </MenuItem>
      </StyledMenu>
    </>
  );

  const renderDrawer = (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={toggleDrawer(false)}
      classes={{ paper: classes.drawerPaper }}
    >
      <Box className={classes.drawerNav}>
        {MOBILE_DRAWER_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = pathname === PATH_APP.general.opportunities
            ? currentTab === item.tab
            : false;
          return (
            <button
              key={item.label}
              type="button"
              className={`${classes.drawerItem} ${
                isActive ? classes.drawerItemActive : ''
              }`}
              onClick={() => {
                setDrawerOpen(false);
                history.push(
                  `${PATH_APP.general.opportunities}?tab=${item.tab}`
                );
              }}
            >
              <Icon
                size={20}
                strokeWidth={2}
                color={isActive ? '#38CFAB' : '#64748B'}
              />
              <span>{item.label}</span>
            </button>
          );
        })}
      </Box>
    </Drawer>
  );

  return (
    <React.Fragment>
      <AppBar position="sticky" color="primary" className={`${classes.desktopBar} ${classes.AppBar}`}>
          <Toolbar disableGutters className={classes.mobileToolbar}>
            <Box className={classes.toolbarContainer}>
              <RouterLink
                to="/app/opportunities"
                style={{
                  display: 'flex',
                  textDecoration: 'none',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <img src={qrata} alt="logo" width="75px" />
                <div
                  style={{ width: '1px', background: 'white', height: '25px' }}
                ></div>
                <Typography style={{ color: 'white' }}>
                  <span style={{ fontSize: '20px' }}>For Talent</span>
                </Typography>
              </RouterLink>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {!logoOnly && renderProfile(false)}
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
      <AppBar position="sticky" className={`${classes.mobileBar} ${classes.mobileHeader}`}>
          <Toolbar className={classes.mobileToolbar}>
            <Box
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              {!logoOnly && (
                <IconButton
                  edge="start"
                  aria-label="menu"
                  aria-haspopup="true"
                  onClick={toggleDrawer(true)}
                  color="#fff !important"
                  style={{ color: '#f7f7f7', width: '30px', height: '30px' }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <RouterLink
                to="/app/opportunities"
                style={{
                  display: 'flex',
                  textDecoration: 'none',
                  color: 'white'
                }}
              >
                <img src={qrata} width="50px" alt="logo" />
              </RouterLink>
            </Box>
            {!logoOnly && renderProfile(true)}
          </Toolbar>
        </AppBar>
      {!logoOnly && renderDrawer}
    </React.Fragment>
  );
}

export default TopBar;
